import { useState, useEffect, useMemo, useCallback, useRef, useId } from 'react';
import { CopyIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import type { RequestLog } from '@mockd/shared';
import type { ActionExecutionResult } from '@mockd/shared/types/action-execution';
import { getContentTypeFromHeaders } from '@mockd/shared/utils';
import { HeadersTable } from './HeadersTable';
import { BodyViewer } from './BodyViewer';
import { parseMatchedRules, resolveRuleName } from './matchedRules';
import { Alert } from '../common/Alert';
import { Cursor, Loading } from '../common/Cursor';
import { MethodBadge } from '../common/MethodBadge';
import { StatusBadge } from '../common/StatusBadge';
import { TabList, tabPanelProps } from '../common/Tabs';
import type { ReplayState } from '../../hooks/useRequestReplay';
import toast from 'react-hot-toast';

interface RequestInspectorProps {
  request: RequestLog;
  mockBaseUrl?: string;
  onClose: () => void;
  replayState?: ReplayState;
  onReplay?: () => void;
  /** Rule id → current name, for resolving matched_rule_ids. */
  ruleNames?: ReadonlyMap<string, string>;
}

type Tab = 'request' | 'response' | 'actions' | 'replay';

function buildCurlCommand(request: RequestLog, mockBaseUrl?: string): string {
  const parts = [`curl -X ${request.method}`];

  try {
    const headers = JSON.parse(request.headers || '{}');
    for (const [key, value] of Object.entries(headers)) {
      const lower = key.toLowerCase();
      if (lower === 'host' || lower === 'content-length' || lower.startsWith('cf-')) continue;
      parts.push(`-H '${key}: ${value}'`);
    }
  } catch {
    // skip malformed headers
  }

  if (request.body && request.body !== 'null') {
    try {
      const parsed = JSON.parse(request.body);
      parts.push(`-d '${JSON.stringify(parsed)}'`);
    } catch {
      parts.push(`-d '${request.body}'`);
    }
  }

  const url = mockBaseUrl ? `${mockBaseUrl}${request.path}` : request.path;
  parts.push(`'${url}'`);
  return parts.join(' \\\n  ');
}

export function RequestInspector({ request, mockBaseUrl, onClose, replayState, onReplay, ruleNames }: RequestInspectorProps) {
  const [activeTab, setActiveTab] = useState<Tab>('request');
  const tabsId = useId();
  const prevReplayStateRef = useRef(replayState);

  const hasReplayResult = (replayState?.response ?? null) !== null || (replayState?.error ?? null) !== null;

  // Reset tab when request changes
  useEffect(() => {
    setActiveTab('request');
  }, [request.id]);

  // Auto-switch to replay tab when a new result arrives
  useEffect(() => {
    const prev = prevReplayStateRef.current;
    const prevHadResult = (prev?.response ?? null) !== null || (prev?.error ?? null) !== null;
    if (hasReplayResult && !prevHadResult) {
      setActiveTab('replay');
    }
    prevReplayStateRef.current = replayState;
  }, [replayState, hasReplayResult]);

  const contentType = useMemo(() => {
    try {
      const headers = JSON.parse(request.headers || '{}');
      return getContentTypeFromHeaders(headers);
    } catch {
      return '';
    }
  }, [request.headers]);

  const handleCopyCurl = useCallback(async () => {
    if (!mockBaseUrl) return;
    try {
      await navigator.clipboard.writeText(buildCurlCommand(request, mockBaseUrl));
      toast.success('cURL command copied');
    } catch {
      toast.error('Copy failed. Try using HTTPS.');
    }
  }, [request, mockBaseUrl]);

  const parsedActionResults = useMemo(() => {
    if (!request.action_results) return null;
    try {
      const results: ActionExecutionResult[] = JSON.parse(request.action_results);
      return results.length > 0 ? results : null;
    } catch {
      return null;
    }
  }, [request.action_results]);

  const matchedRules = useMemo(() => parseMatchedRules(request), [request]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'request', label: 'Request' },
    { key: 'response', label: 'Response' },
    ...(parsedActionResults ? [{ key: 'actions' as Tab, label: 'Actions' }] : []),
    { key: 'replay', label: 'Replay' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 shrink-0">
        <MethodBadge method={request.method} />
        <StatusBadge status={request.response_status} />
        <span className="font-mono text-sm text-base-content truncate flex-1">
          {request.path}
        </span>
        {onReplay && (
          <button
            onClick={onReplay}
            disabled={replayState?.isReplaying}
            className={`btn btn-ghost btn-xs ${hasReplayResult ? 'text-success' : ''}`}
            title="Replay request"
            aria-label="Replay request"
          >
            {replayState?.isReplaying ? (
              <Cursor />
            ) : (
              <RefreshCwIcon className="size-3.5" />
            )}
          </button>
        )}
        {mockBaseUrl && (
          <button
            onClick={handleCopyCurl}
            className="btn btn-ghost btn-xs"
            title="Copy as cURL"
            aria-label="Copy as cURL"
          >
            <CopyIcon className="size-3.5" />
          </button>
        )}
        <button
          onClick={onClose}
          className="btn btn-ghost btn-xs btn-square"
          title="Close inspector"
          aria-label="Close inspector"
        >
          <XIcon className="size-4" />
        </button>
      </div>

      {/* Tabs */}
      <TabList
        tabs={tabs}
        active={activeTab}
        onChange={setActiveTab}
        idPrefix={tabsId}
        className="tabs tabs-border tabs-sm px-4 shrink-0"
      />

      {/* Tab content */}
      <div {...tabPanelProps(tabsId, activeTab)} className="flex-1 overflow-y-auto p-4">
        {activeTab === 'request' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-base-content/70 uppercase tracking-wider mb-2">Headers</h3>
              <HeadersTable data={request.headers} />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-base-content/70 uppercase tracking-wider mb-2">Body</h3>
              {request.body || request.parsed_body ? (
                <BodyViewer body={request.body} contentType={contentType} parsedBody={request.parsed_body} />
              ) : (
                <p className="text-sm text-base-content/70">No request body</p>
              )}
            </div>
          </div>
        )}
        {activeTab === 'response' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-base-content/70 uppercase tracking-wider mb-2">Matched Rules</h3>
              {matchedRules.length > 0 ? (
                <ol className="space-y-1.5">
                  {matchedRules.map((rule, i) => {
                    const name = resolveRuleName(rule.id, ruleNames, request);
                    return (
                      <li key={rule.id} className="flex flex-wrap items-center gap-2 text-sm bg-base-200 rounded-field px-2.5 py-1.5">
                        <span className="text-xs text-base-content/70 font-mono w-4 shrink-0">{i + 1}.</span>
                        <span className={`font-medium truncate min-w-0 ${name ? '' : 'text-base-content/70 italic'}`}>
                          {name ?? 'Deleted rule'}
                        </span>
                        <span className="text-xs text-base-content/70 font-mono truncate min-w-0">{rule.id}</span>
                        <span className="flex items-center gap-1 ml-auto">
                          {rule.capturedResponse ? (
                            <span className="badge badge-primary badge-sm">Response</span>
                          ) : (
                            <span className="badge badge-ghost badge-sm">Actions only</span>
                          )}
                          {rule.actionsSkipped && (
                            <span className="badge badge-warning badge-sm">Actions skipped (Pro)</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="text-sm text-base-content/70">
                  No rules matched — the endpoint's default response was returned.
                </p>
              )}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-base-content/70 uppercase tracking-wider mb-2">Headers</h3>
              <HeadersTable data={request.response_headers} />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-base-content/70 uppercase tracking-wider mb-2">Body</h3>
              {request.response_body ? (
                <BodyViewer
                  body={request.response_body}
                  contentType={(() => {
                    try {
                      const headers = JSON.parse(request.response_headers || '{}');
                      return getContentTypeFromHeaders(headers);
                    } catch {
                      return '';
                    }
                  })()}
                />
              ) : (
                <p className="text-sm text-base-content/70">No response body</p>
              )}
            </div>
          </div>
        )}
        {activeTab === 'actions' && parsedActionResults && (
          <div className="space-y-1.5">
            {parsedActionResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-mono bg-base-200 rounded-field px-2.5 py-1.5">
                <span className="text-base-content/70">{r.actionType}</span>
                <span className={`badge badge-xs ${
                  r.status === 'success' ? 'badge-success' :
                  r.status === 'error' ? 'badge-error' :
                  r.status === 'timeout' ? 'badge-warning' :
                  'badge-ghost'
                }`}>
                  {r.status}
                </span>
                <span className="text-xs text-base-content/70 ml-auto">{r.durationMs}ms</span>
                {r.error && (
                  <span className="text-xs text-error truncate max-w-[200px]" title={r.error}>{r.error}</span>
                )}
              </div>
            ))}
          </div>
        )}
        {activeTab === 'replay' && (
          <div className="space-y-3">
            {replayState?.isReplaying ? (
              <Loading label="Replaying request…" className="py-8" />
            ) : replayState?.error ? (
              <Alert variant="error" title="Replay failed">{replayState.error}</Alert>
            ) : replayState?.response ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <StatusBadge status={replayState.response.status} />
                  <span className="text-xs text-base-content/70 font-mono">
                    {replayState.response.duration}ms
                  </span>
                </div>
                {replayState.response.body && (
                  <pre className="bg-base-200 border border-base-300 p-3 rounded-box text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto">
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(replayState.response!.body), null, 2);
                      } catch {
                        return replayState.response!.body;
                      }
                    })()}
                  </pre>
                )}
              </div>
            ) : (
              <div className="text-sm text-base-content/70 text-center py-8">
                Use the replay button above to re-send this request.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
